/*
  Vitals — silent location resolution (web).

  Resolves the visitor's approximate address (street / city / province / postal code) so a guest's
  booking form and checkout can be pre-filled the moment the page loads. `resolve()` never shows a
  permission prompt; only `requestPrecise()` does, and only where a call site opts into it. Two
  sources, in order:

    1. `navigator.geolocation` — used ONLY when the Permissions API reports the geolocation
       permission is already `granted`. A granted permission means no prompt is shown, and the fix is
       precise enough to carry a street address.
    2. Google Geolocation API (`geolocation/v1/geolocate` with `considerIp: true`) — an IP-derived
       fix. No prompt, no permission, works everywhere, but only accurate to city/district level.

  The fix is then reverse-geocoded with the Google Geocoding API into Shopify-shaped address parts.
  `precision` reports which source won: `"precise"` fixes carry `address1`, `"approximate"` (IP)
  fixes deliberately do NOT — an invented street line on a home-collection booking is worse than a
  blank one, while the city / province / postal code are still useful as checkout prefill. The
  district and subdistrict are exposed separately either way, so a caller that wants a coarse street
  line for checkout can build one without the booking form having to show it.

  Requires the Google API key to have BOTH the "Geolocation API" and the "Geocoding API" enabled
  (the Maps JS key used for Places autocomplete usually only has Places + Maps JS). Every failure
  path resolves to `null` — this is a convenience layer and must never block a booking.

  Usage:
    VitalsSilentLocation.resolve(apiKey).then(function (loc) { ... });      // silent lookup
    VitalsSilentLocation.parsePlace(place);                                 // a Places selection
    VitalsSilentLocation.requestPrecise(apiKey);                            // the one prompting call
    VitalsSilentLocation.debug('stage', detail);                            // ?vitals_debug=1 only
*/
(function () {
  'use strict';

  if (window.VitalsSilentLocation) return;

  var CACHE_KEY = '_vitals_geo_v2';
  var CACHE_TTL_MS = 12 * 60 * 60 * 1000;

  /**
   * A cached "nothing here" expires far sooner than a cached address. The only negative we store is
   * the out-of-country verdict, and that is exactly the one that goes stale for a reason we can't
   * observe — a VPN switching off, a phone crossing a border, a mis-geolocated ISP range. Twelve
   * hours of pre-filling nothing because of one bad lookup is too long.
   */
  var NEGATIVE_TTL_MS = 30 * 60 * 1000;

  /**
   * The only country this storefront sells into. A detected location outside it is USELESS and
   * actively harmful: Shopify discards a Philippine address on a Thailand-only shop, and pairing one
   * with `countryCode: TH` invalidates the whole buyer identity — which is exactly why a shopper
   * abroad saw nothing populate at all. Outside Thailand we now resolve to null and pre-fill nothing,
   * which is the honest answer.
   */
  var SERVICE_COUNTRY = 'TH';

  /** A device fix tighter than this (metres) is treated as precise enough for a street line. */
  var PRECISE_ACCURACY_M = 300;

  /** Shared promise so several sections on one page trigger at most one lookup. */
  var inFlight = null;

  /**
   * A device fix obtained the moment the shopper granted permission, consumed by the next `fixFor()`
   * so the upgrade doesn't spend a second `getCurrentPosition` on a position we already hold.
   */
  var freshDeviceFix = null;

  /**
   * Bumped whenever a permission grant makes an in-progress lookup obsolete. A lookup only writes the
   * cache and clears `inFlight` if its generation is still current, so the approximate answer that
   * was already in flight can't land on top of the precise one that replaced it.
   */
  var generation = 0;

  var OVERRIDE_KEY = '_vitals_geo_override';
  var DEBUG_KEY = '_vitals_geo_debug';
  var ASKED_KEY = '_vitals_geo_asked';

  function forget(key) {
    try { sessionStorage.removeItem(key); } catch (e) {}
  }

  function elapsedClock() {
    try {
      if (window.performance && performance.now) return function () { return performance.now(); };
    } catch (e) {}
    return function () { return Date.now(); };
  }

  var clock = elapsedClock();
  var startedAt = clock();

  /*
    ?vitals_debug=1 narrates every stage of the lookup to the console; ?vitals_debug=off stops it.
    Persisted for the session like the override below, and for the same reason — the theme rewrites
    the URL out from under us, so a flag read lazily would vanish on the first variant change.

    This exists because the silent path had no observability at all: when it produced an empty
    checkout there was no way to tell whether the fix, the geocode, the country gate, the province
    match or the cart mirror was the one that gave up. Every `debug()` line below marks one of those
    decision points, timestamped, so the failing stage names itself.
  */
  var debugOn = (function captureDebug() {
    var search = '';
    try { search = window.location.search || ''; } catch (e) { return false; }
    if (/[?&]vitals_debug=off\b/.test(search)) { forget(DEBUG_KEY); return false; }
    if (/[?&]vitals_debug=1\b/.test(search)) {
      try { sessionStorage.setItem(DEBUG_KEY, '1'); } catch (e) {}
      return true;
    }
    try { return sessionStorage.getItem(DEBUG_KEY) === '1'; } catch (e) { return false; }
  })();

  function debug(stage, detail) {
    if (!debugOn || !window.console || !console.info) return;
    var stamp = '[vitals-geo +' + Math.round(clock() - startedAt) + 'ms]';
    if (detail === undefined) console.info(stamp, stage);
    else console.info(stamp, stage, detail);
  }

  /**
   * Returns `undefined` for "no entry", or the cached value — which may legitimately be `null`,
   * meaning "we looked, and there's nothing usable here". Caching that verdict matters: without it
   * every page view by a shopper abroad would spend two more Google calls to reach the same no.
   */
  function readCache() {
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return undefined;
      var entry = JSON.parse(raw);
      if (!entry || !entry.at) return undefined;
      var ttl = entry.value ? CACHE_TTL_MS : NEGATIVE_TTL_MS;
      if (Date.now() - entry.at > ttl) return undefined;
      return entry.value;
    } catch (e) { return undefined; }
  }

  function writeCache(value) {
    try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), value: value })); } catch (e) {}
  }

  /**
   * Shopify's Latin name for a province, or `''` when there isn't one. Google's name for the same
   * place is not stable — prefixed ("Chang Wat Chiang Mai"), spaced differently ("Chonburi" vs
   * "Chon Buri"), or still in Thai script even with `language=en` — so the whole mapping lives in
   * assets/vitals-thai-provinces.js, shared with the booking sections.
   *
   * If that asset didn't load we fall back to the old behaviour: keep a Latin name and drop Thai
   * script, because a province Shopify can't match is worse than an empty one — it can file the
   * address under the wrong zone without complaining.
   */
  function normalizeProvince(raw) {
    var api = window.VitalsThaiProvinces;
    if (api && api.canonical) return api.canonical(raw);
    var value = String(raw || '').replace(/^\s*จังหวัด\s*/, '');
    value = value.replace(/^\s*(?:chang\s*wat|province\s+of)\s+/i, '').trim();
    return /^[\x20-\x7E]+$/.test(value) ? value : '';
  }

  /** Strip the administrative noise Google puts on a Thai city / district name. */
  function cleanLocality(raw) {
    var api = window.VitalsThaiProvinces;
    if (api && api.cleanLocality) return api.cleanLocality(raw);
    return String(raw || '').trim();
  }

  /** First address component carrying `type`, as its long name. */
  function pick(components, type) {
    for (var i = 0; i < components.length; i++) {
      var types = components[i].types || [];
      if (types.indexOf(type) !== -1) return components[i].long_name || '';
    }
    return '';
  }

  /** Same, but the ISO short name — "TH" rather than "Thailand". */
  function pickShort(components, type) {
    for (var i = 0; i < components.length; i++) {
      var types = components[i].types || [];
      if (types.indexOf(type) !== -1) return components[i].short_name || '';
    }
    return '';
  }

  /**
   * Map Google `address_components` onto the fields Shopify's address form needs. Thai addresses put
   * the district in `sublocality_level_1` and the (Bangkok) city in `locality` /
   * `administrative_area_level_2`, so both are tried before giving up on `city`.
   */
  function fromComponents(components) {
    components = components || [];
    var streetNumber = pick(components, 'street_number');
    var route = pick(components, 'route');
    // A Thai address nests two levels below the province: the khwaeng (subdistrict) and the khet or
    // amphoe (district). Google files them as `sublocality_level_2` and `sublocality_level_1` — and
    // for a Bangkok address it returns NO `locality` and NO `administrative_area_level_2` at all.
    // That is exactly why a City taken straight off `locality` is blank for every Bangkok address:
    //   29 Chit Lom Rd  →  sublocality_level_2 "Khwaeng Lumphini"
    //                      sublocality_level_1 "Pathum Wan"
    //                      administrative_area_level_1 "Krung Thep Maha Nakhon"
    var subdistrict = cleanLocality(pick(components, 'sublocality_level_2'));
    var district = cleanLocality(pick(components, 'sublocality_level_1') || pick(components, 'sublocality'));
    var locality = cleanLocality(pick(components, 'locality') || pick(components, 'administrative_area_level_2'));
    var street = [streetNumber, route].filter(Boolean).join(' ');
    return {
      // The khwaeng belongs on the street line, which is how a Thai address is written.
      address1: [street, subdistrict].filter(Boolean).join(', '),
      // Exposed in their own right so a caller can compose a coarse street line for checkout out of
      // them without the booking form having to show one. See vitals-checkout-prefill.js.
      subdistrict: subdistrict,
      district: district,
      city: locality || district || subdistrict,
      province: normalizeProvince(pick(components, 'administrative_area_level_1')),
      zip: pick(components, 'postal_code'),
      country: pick(components, 'country') || 'Thailand',
      countryCode: pickShort(components, 'country')
    };
  }

  /**
   * Structured parts for a place the customer picked in Places autocomplete. Always `"precise"` —
   * the customer chose this address themselves, so its street line is trustworthy. Requires
   * `address_components` in the Autocomplete `fields` list.
   */
  function parsePlace(place) {
    if (!place) return null;
    var components = place.address_components || [];
    var parts = fromComponents(components);

    var name = place.name || '';
    var formatted = place.formatted_address || '';
    if (name && formatted && formatted.indexOf(name) === -1) formatted = name + ', ' + formatted;
    else if (name && !formatted) formatted = name;

    // Prefix the place's own name only for an establishment ("Vanissa Building, 29 Chit Lom Rd").
    // For a plain street address Google sets `name` to the street itself, so prefixing there would
    // duplicate it — "29 Chit Lom Rd, 29 Chit Lom Road". Needs 'types' in the Autocomplete fields.
    var types = place.types || [];
    var isEstablishment = types.indexOf('establishment') !== -1 || types.indexOf('premise') !== -1;
    if (name && isEstablishment && parts.address1.indexOf(name) === -1) {
      parts.address1 = parts.address1 ? name + ', ' + parts.address1 : name;
    }
    // Nothing structured to build a street line from — fall back to what Google printed.
    if (!parts.address1) parts.address1 = formatted;
    parts.formatted = formatted;
    parts.precision = 'precise';
    return parts;
  }

  /** Parse `vitals_geo=lat,lng[,accuracyMetres]` out of a query string. */
  function parseOverride(search) {
    var match = /[?&]vitals_geo=(-?[\d.]+),(-?[\d.]+)(?:,(\d+))?/.exec(search || '');
    if (!match) return null;
    return {
      lat: parseFloat(match[1]),
      lng: parseFloat(match[2]),
      accuracy: match[3] ? parseInt(match[3], 10) : 50
    };
  }

  /**
   * `?vitals_geo=13.7440,100.5430` (optionally `,accuracyMetres`) forces a fix — the only way to
   * exercise this feature from outside Thailand, where the real IP fix correctly resolves to
   * nothing. `?vitals_geo=off` clears it.
   *
   * It overrides ONLY where the latitude and longitude come from. Everything downstream — the
   * session cache, the shared in-flight promise, the precision classification — runs exactly as it
   * does for a real shopper, because an override that skipped all of that was testing a different
   * code path than the one that was broken. Note the third parameter: leave it off and the fix is
   * classified `precise` (50 m, a device-grade fix); pass a realistic IP accuracy such as
   * `?vitals_geo=13.7563,100.5018,16000` to exercise the `approximate` branch that real shoppers
   * actually get.
   *
   * Captured ONCE here at script load and remembered for the rest of the session, because the theme
   * rewrites the URL out from under us: `assets/variants.js` calls
   * `replaceState(productUrl + '?variant=' + id)` on the product page, which drops every other query
   * parameter. Reading the parameter lazily at click time therefore found nothing in a real browser —
   * it only ever worked in tests, where variants.js isn't running. Persisting it also means you set
   * it once and can then browse normally.
   */
  var capturedOverride = (function captureOverride() {
    var search = '';
    try { search = window.location.search || ''; } catch (e) { return null; }

    if (/[?&]vitals_geo=off\b/.test(search)) {
      forget(OVERRIDE_KEY);
      // Also drop the resolved address, or the override's own cached result outlives the override.
      forget(CACHE_KEY);
      return null;
    }

    var fromUrl = parseOverride(search);
    if (fromUrl) {
      var serialized = JSON.stringify(fromUrl);
      var previous = null;
      try { previous = sessionStorage.getItem(OVERRIDE_KEY); } catch (e) {}
      // Only when the coordinates actually change, so reloading the same URL still exercises the
      // cache-hit path a real shopper takes on their second page view.
      if (previous !== serialized) forget(CACHE_KEY);
      try { sessionStorage.setItem(OVERRIDE_KEY, serialized); } catch (e) {}
      return fromUrl;
    }
    try {
      var stored = sessionStorage.getItem(OVERRIDE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (e) { return null; }
  })();

  function overrideFix() {
    // Re-read the live URL as a fallback, in case this script somehow ran after the rewrite.
    if (capturedOverride) return capturedOverride;
    try { return parseOverride(window.location.search); } catch (e) { return null; }
  }

  /**
   * The browser fix, but only when the permission is ALREADY granted — anything else would surface
   * a prompt, which this module exists to avoid. Resolves `null` whenever it can't tell.
   */
  function grantedBrowserFix() {
    return new Promise(function (resolve) {
      if (!navigator.geolocation || !navigator.permissions || !navigator.permissions.query) {
        resolve(null);
        return;
      }
      var settled = false;
      function done(value) { if (!settled) { settled = true; resolve(value); } }
      navigator.permissions.query({ name: 'geolocation' })
        .then(function (status) {
          if (!status || status.state !== 'granted') {
            debug('device fix: permission not granted, skipping', status && status.state);
            done(null);
            return;
          }
          navigator.geolocation.getCurrentPosition(
            function (pos) {
              done({
                lat: pos.coords.latitude,
                lng: pos.coords.longitude,
                accuracy: pos.coords.accuracy || 0
              });
            },
            function (err) { debug('device fix: failed', err && err.message); done(null); },
            // High accuracy is the entire point of this branch: a network fix names a
            // neighbourhood, a GPS fix names a doorstep, and only the latter earns a street line.
            // `maximumAge` means a fix taken minutes ago on another page is reused for free.
            { enableHighAccuracy: true, timeout: 8000, maximumAge: 10 * 60 * 1000 }
          );
        })
        .catch(function () { debug('device fix: Permissions API unavailable'); done(null); });
      // Safari can leave `getCurrentPosition` hanging even on a granted permission. Must outlast the
      // timeout above, or it would cut off a GPS fix that was still perfectly on track to arrive.
      setTimeout(function () { done(null); }, 10000);
    });
  }

  /** IP-derived fix from the Google Geolocation API. No prompt, no permission. */
  function ipFix(apiKey) {
    return fetch('https://www.googleapis.com/geolocation/v1/geolocate?key=' + encodeURIComponent(apiKey), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ considerIp: true })
    })
      .then(function (res) {
        if (res.ok) return res.json();
        debug('IP fix: HTTP ' + res.status + ' from the Geolocation API');
        return null;
      })
      .then(function (json) {
        if (!json || !json.location) return null;
        return { lat: json.location.lat, lng: json.location.lng, accuracy: json.accuracy || 0 };
      })
      .catch(function (e) {
        debug('IP fix: request failed', e && e.message);
        return null;
      });
  }

  /**
   * Where the latitude/longitude comes from, in preference order: a testing override, an
   * already-granted device permission, then the IP lookup.
   */
  function fixFor(apiKey) {
    if (freshDeviceFix) {
      var granted = freshDeviceFix;
      freshDeviceFix = null;
      debug('fix: permission just granted', granted);
      return Promise.resolve(granted);
    }
    var override = overrideFix();
    if (override) {
      debug('fix: testing override', override);
      return Promise.resolve(override);
    }
    return grantedBrowserFix().then(function (fix) {
      if (fix) {
        debug('fix: granted device permission', fix);
        return fix;
      }
      return ipFix(apiKey).then(function (ipResult) {
        debug(ipResult ? 'fix: IP geolocation' : 'fix: IP geolocation gave nothing', ipResult);
        return ipResult;
      });
    });
  }

  /**
   * Reverse-geocode a fix. Always requested in English so `province` matches the province names
   * Shopify expects on a Thai address, regardless of the storefront locale.
   */
  function reverseGeocode(apiKey, fix) {
    var url = 'https://maps.googleapis.com/maps/api/geocode/json' +
      '?latlng=' + encodeURIComponent(fix.lat + ',' + fix.lng) +
      '&language=en' +
      '&key=' + encodeURIComponent(apiKey);
    return fetch(url)
      .then(function (res) {
        if (res.ok) return res.json();
        debug('geocode: HTTP ' + res.status + ' from the Geocoding API');
        return null;
      })
      .then(function (json) {
        var results = (json && json.results) || [];
        if (!results.length) {
          debug('geocode: no results', json && (json.error_message || json.status));
          return null;
        }
        var parts = fromComponents(results[0].address_components);
        debug('geocode: raw province from Google', pick(results[0].address_components, 'administrative_area_level_1'));
        // A broad fix often lands on a result with no postal code; later results are wider areas
        // that usually carry one. Same for the province, and for the district pair — a coarse fix
        // can resolve to a plus code that carries no sublocality at all.
        for (var i = 1; i < results.length; i++) {
          if (parts.zip && parts.province && parts.city && parts.district) break;
          var wider = fromComponents(results[i].address_components);
          if (!parts.zip) parts.zip = wider.zip;
          if (!parts.province) parts.province = wider.province;
          if (!parts.city) parts.city = wider.city;
          if (!parts.district) parts.district = wider.district;
          if (!parts.subdistrict) parts.subdistrict = wider.subdistrict;
        }
        parts.formatted = results[0].formatted_address || '';
        return parts;
      })
      .catch(function (e) {
        debug('geocode: request failed', e && e.message);
        return null;
      });
  }

  /**
   * Resolve the visitor's address silently. Cached per session (12h) so repeat page views cost
   * nothing. Resolves `null` when there's no key, no fix, or anything at all goes wrong.
   */
  function resolve(apiKey) {
    if (!apiKey) {
      debug('resolve: no Google API key configured, feature is off');
      return Promise.resolve(null);
    }

    var cached = readCache();
    if (cached !== undefined) {
      debug('resolve: session cache hit', cached);
      return Promise.resolve(cached);
    }
    if (inFlight) {
      debug('resolve: joining the lookup already in flight');
      return inFlight;
    }
    return beginLookup(apiKey);
  }

  /**
   * Run a lookup, ignoring whatever is cached. Only `resolve()` (cache first) and the permission
   * upgrade (which must not be served the stale approximate answer it is trying to replace) call it.
   */
  function beginLookup(apiKey) {
    // A permission grant mid-lookup makes this lookup obsolete; `superseded` lets it notice.
    var gen = generation;
    function superseded() {
      if (gen === generation) return false;
      debug('resolve: superseded by a permission grant, discarding this result');
      return true;
    }

    var promise = fixFor(apiKey)
      .then(function (fix) {
        if (!fix) {
          debug('resolve: no fix from any source, pre-filling nothing');
          return null;
        }
        return reverseGeocode(apiKey, fix).then(function (parts) {
          if (!parts) return null;

          // Outside the country this shop sells into there is nothing worth pre-filling, and sending
          // it would break the buyer identity outright. Cache the no so we stop asking Google.
          if (parts.countryCode && parts.countryCode !== SERVICE_COUNTRY) {
            debug('resolve: fix is in ' + parts.countryCode + ', not ' + SERVICE_COUNTRY + ' — nothing to pre-fill');
            if (!superseded()) writeCache(null);
            return null;
          }

          var precise = fix.accuracy > 0 && fix.accuracy <= PRECISE_ACCURACY_M;
          parts.precision = precise ? 'precise' : 'approximate';
          parts.accuracy = fix.accuracy;
          // An IP fix pins a neighbourhood, not a doorstep — publishing its street line would put a
          // wrong address on a home visit. City / province / postal code stay, they're still right,
          // and `district`/`subdistrict` remain available for callers that want a coarse street line.
          if (!precise) parts.address1 = '';
          if (!parts.province) {
            debug('resolve: no province Shopify can match — the address will go without one');
          }
          if (superseded()) return null;
          writeCache(parts);
          debug('resolve: resolved (' + parts.precision + ', ' + Math.round(fix.accuracy) + 'm)', parts);
          return parts;
        });
      })
      // A transient failure is deliberately NOT cached — only a definitive out-of-country verdict is.
      .catch(function (e) {
        debug('resolve: threw, pre-filling nothing', e && e.message);
        return null;
      });

    inFlight = promise.then(function (value) {
      if (gen === generation) inFlight = null;
      return value;
    });
    return inFlight;
  }

  /**
   * Ask for the geolocation permission, and on a grant re-resolve at street-level precision.
   *
   * This is the ONLY thing in this module that can surface a prompt, and it is opt-in per call site —
   * everything else stays silent. It exists because IP geolocation is capped at roughly city level
   * (measured: ~16 km for this shop's key), so a real street address is unobtainable without the
   * device. A granted permission also makes every LATER visit precise for free, because
   * `grantedBrowserFix()` picks it up with no prompt at all.
   *
   * Asks at most once per session, and never after a refusal. Both matter: browsers treat repeated
   * dismissals as a block, and a block is sticky — it would kill the precise path for that shopper
   * permanently, on a shop where the whole feature depends on it.
   *
   * Resolves the precise parts on a grant, or `null` for every other outcome (already granted,
   * previously denied, dismissed, unsupported, timed out). Never rejects.
   */
  function requestPrecise(apiKey) {
    if (!apiKey || !navigator.geolocation) return Promise.resolve(null);

    var state = (navigator.permissions && navigator.permissions.query)
      ? navigator.permissions.query({ name: 'geolocation' })
          .then(function (status) { return status && status.state; })
          .catch(function () { return null; })
      : Promise.resolve(null);

    return state.then(function (current) {
      if (current === 'granted') {
        // Already allowed, so no prompt is needed — but a coarse answer may already be cached from
        // an IP lookup that finished first. Redo it; `grantedBrowserFix()` supplies the device fix
        // silently. Checked before the once-per-session guard below, because this asks nothing.
        var cached = readCache();
        if (cached && cached.precision === 'approximate') {
          debug('permission: granted, but the cached answer is IP-level — re-resolving precisely');
          generation++;
          inFlight = null;
          return beginLookup(apiKey);
        }
        debug('permission: already granted — the silent path is already precise');
        return null;
      }
      if (current === 'denied') {
        debug('permission: refused previously, not asking again');
        return null;
      }

      // Everything past here shows a prompt, so this is where "ask at most once" belongs.
      try {
        if (sessionStorage.getItem(ASKED_KEY)) {
          debug('permission: already asked once this session, not asking again');
          return null;
        }
      } catch (e) {}

      try { sessionStorage.setItem(ASKED_KEY, '1'); } catch (e) {}
      debug('permission: asking for a precise fix');

      return new Promise(function (done) {
        var settled = false;
        function finish(value) { if (!settled) { settled = true; done(value); } }

        navigator.geolocation.getCurrentPosition(
          function (pos) {
            var fix = {
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
              accuracy: pos.coords.accuracy || 0
            };
            debug('permission: granted, upgrading to a precise address', fix);
            // Hand the position straight to the next lookup and orphan anything already in flight so
            // it can't overwrite what we are about to store.
            //
            // The cached IP answer is deliberately NOT cleared first. If this precise lookup fails —
            // a geocode error, a dead network — clearing it would leave the shopper with nothing at
            // all, which is worse than the district-level prefill they already had. It is overwritten
            // on success instead, so the fallback survives right up until there is something better.
            freshDeviceFix = fix;
            generation++;
            inFlight = null;
            beginLookup(apiKey).then(finish, function () { finish(null); });
          },
          function (err) {
            debug('permission: not granted', err && err.message);
            finish(null);
          },
          // No `maximumAge`: the shopper just opted in, so it is worth waiting for a real reading
          // rather than reusing whatever coarse position happened to be lying around.
          { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
        );

        // Safari can leave `getCurrentPosition` hanging with neither callback ever firing.
        setTimeout(function () { finish(null); }, 18000);
      });
    });
  }

  if (capturedOverride) {
    debug('location override active — ?vitals_geo=off to clear', capturedOverride);
    if (!debugOn && window.console && console.info) {
      console.info('[vitals] location override active:', capturedOverride, '— ?vitals_geo=off to clear');
    }
  }

  window.VitalsSilentLocation = {
    resolve: resolve,
    parsePlace: parsePlace,
    fromComponents: fromComponents,
    requestPrecise: requestPrecise,
    debug: debug
  };
})();
